import { type ImageJob, operationSpec } from '@acasa/shared'
import { useEffect, useState } from 'react'

/**
 * Elapsed time against the operation's expected duration.
 *
 * One Gemini call reports no progress, so there is nothing real to show. This
 * is openly an ESTIMATE — the bar is driven by the clock, the label says "ca.",
 * and it stops short of full because a bar sitting at 100% while nothing has
 * arrived reads as a hang. When a job runs past its estimate it says so instead
 * of pretending to be nearly done.
 */
export function JobProgress({ job }: { job: ImageJob }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const expectedMs = operationSpec(job.operation).durationMs
  const startedAt = job.startedAt ? Date.parse(job.startedAt) : null
  const elapsedMs = startedAt === null ? 0 : Math.max(0, now - startedAt)
  const overrun = elapsedMs > expectedMs
  const ratio = startedAt === null ? 0 : Math.min(0.92, elapsedMs / expectedMs)

  const seconds = Math.round(elapsedMs / 1000)
  const expectedSeconds = Math.round(expectedMs / 1000)

  return (
    <div className="px-4 py-4">
      <div className="bg-open h-1 w-full overflow-hidden">
        <div
          className="bg-brand h-full transition-[width] duration-1000 ease-linear"
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      <p className="text-faint mt-2 text-[11px]">
        {job.status === 'queued'
          ? 'Wartet auf einen freien Platz …'
          : overrun
            ? `läuft seit ${seconds} s — dauert länger als üblich`
            : `läuft seit ${seconds} s · ca. ${expectedSeconds} s`}
      </p>
    </div>
  )
}
